/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  CompanyContainer,
  HeaderTitle,
  IconButton,
  LoaderWrapper,
  StyledContainer,
  TableCell,
  TableWrapper,
} from '../../../services/settings/appStyle/global.style';
import {
  Table,
  IconButton as IconButtonWithText,
  Loader,
  Label,
} from '../../../Components/uielements';
import { useUserContext } from '../../../hooks/UserContext';
import { AddPlusIcon, DownloadIconWithoutBg } from '../../../Components/icons';
import CVDServices from '../../../services/bioplus-services/cvd';
import moment from 'moment';
import { fetchApiData } from '../../../Components/Api';
import { downloadPdfFromBase64 } from '../../../Components/Base64toBlob';
import { UsaAddCvdModal, AusAddCvdModal } from './modals';
import { getCvdValues } from './helper';
import { IntlMessages } from '../../../helper/langHelper';
import { getCountryCode } from '../UserInvitation/userInvitation';

const Cvd = () => {
  const userContext = useUserContext();
  const userRole = userContext?.company;
  const companyId = userRole?.companyId?.id;

  const { userPermission } = userContext;
  const isUserAllowToManage = userPermission?.find((permission) => permission?.label === 'edec');

  // const canManageEdec = !!isUserAllowToManage?.isManage;
  const canCreateEdec = !!isUserAllowToManage?.isCreate;

  const initialFormData = getCvdValues(process.env.REACT_APP_ENV);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [initialFormDataState, setInitialFormDataState] = useState(initialFormData);
  const [loader, setLoader] = useState(false);
  const [cvdFormData, setCvdFormData] = useState(initialFormDataState);
  const [cvdModal, setCvdModal] = useState(false);
  // Tracks whether a CVD create request is currently in-flight (used to
  // disable the submit button and show "Submitting..." in the modal)
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Bumped every time the modal is opened/closed so the modal component is
  // fully remounted, resetting all uncontrolled fields (defaultValue), the
  // multistep "expanded" index, and the signature canvas.
  const [cvdModalKey, setCvdModalKey] = useState(0);

  const query = useQuery(['listOfCvds', page, pageSize], () =>
    CVDServices.listOfCVDS(companyId, page, pageSize)
  );
  const tableData = query?.data?.items ?? [];

  const handleNextPage = () => {
    setPage((value) => value + 1);
  };
  const handlePreviousPage = () => {
    setPage((value) => value - 1);
  };

  //   table coulmns
  const userColumns = useMemo(
    () => [
      // {
      //   Header: <IntlMessages id="table.col.propertyName" />,
      //   accessor: 'propertyName',
      //   Cell: (cellData) => <TableCell>{cellData.value}</TableCell>,
      //   width: '20%',
      // },
      {
        Header: <IntlMessages id="table.col.createdBy" />,
        accessor: 'createdBy',
        width: '25%',
      },
      {
        Header: <IntlMessages id="table.col.buyerName" />,
        accessor: 'buyerName',
      },
      {
        Header: <IntlMessages id="table.col.premise" />,
        accessor: 'premiseId.propertyName',
        // Cell: (cellData) => (
        //   <TableCell>{cellData.value ?? cellData?.row?.original?.premiseId}</TableCell>
        // ),
      },
      {
        Header: <IntlMessages id="table.col.transporter" />,
        accessor: 'transporterName',
      },
      {
        Header: <IntlMessages id="table.col.date" />,
        accessor: 'createdAt',
        Cell: (cellData) => <TableCell>{moment(cellData.value).format('DD-MM-YYYY')}</TableCell>,
      },
      {
        Header: <IntlMessages id="table.col.downloadPdf" />,
        accessor: 'DownloadPdf',
        isAction: true,
      },
    ],
    []
  );
  const countryPhoneCode = getCountryCode(process.env.REACT_APP_ENV);

  const getUserData = async () => {
    setLoader(true);
    fetchApiData('users/self', localStorage.getItem('session'))
      .then((data) => {
        fetchApiData(`/companies/${companyId}`, localStorage.getItem('session')).then((res) => {
          setInitialFormDataState({
            ...initialFormDataState,
            vendorName: data.data.firstName + ' ' + data.data.lastName,
            vendorContact: data.data.phone || countryPhoneCode,
            vendorEmail: data.data.email,
            organisationName: data.data.employerCompany,
            vendorAddress: res?.data?.addressStreet ?? '',
            vendorTown: res?.data?.addressTown ?? '',
            vendorState: res?.data?.addressState ?? '',
          });
        });
      })
      .finally(() => setLoader(false));
  };

  // Tracks which row(s) currently have a PDF download in progress, so we can
  // disable that row's download button and show "Downloading..." instead of
  // leaving it clickable (and silent) while the request is in-flight.
  const [downloadingIds, setDownloadingIds] = useState([]);

  const handleDownloadPdf = (data) => {
    if (data) {
      const rowId = data?.documentId;
      setDownloadingIds((prev) => [...prev, rowId]);
      fetchApiData(`/documents/${data?.documentId}`, localStorage.getItem('session'))
        .then((data) => {
          const day_date_time = moment(data?.data?.createdAt ?? moment()).format(
            'ddd-DD-MM-YYYY-hh.mm A'
          );
          let fileName = `Cvd-${userRole?.companyName ?? ''}-${day_date_time}.pdf`;
          let base64 = data?.data?.base64File?.replace(/^data:application\/pdf;base64,/, '');
          downloadPdfFromBase64(base64, fileName).catch((error) => console.log(error, 'error...'));
        })
        .catch((error) => console.log(error, 'error...'))
        .finally(() => {
          setDownloadingIds((prev) => prev.filter((id) => id !== rowId));
        });
    }
  };

  // Resets the form data to a fresh copy of the initial values, closes/opens
  // the modal, and bumps the remount key so the modal (and all its
  // uncontrolled inputs, step index, and signature pad) start from scratch
  // every single time — whether it was just opened, cancelled, or submitted.
  const toggleCvdModal = () => {
    setCvdFormData({ ...initialFormDataState });
    setCvdModal((prev) => !prev);
    setCvdModalKey((prev) => prev + 1);
  };

  const actionList = [
    (data) => {
      const isRowDownloading = downloadingIds.includes(data?.documentId);
      return (
        <IconButton
          onClick={() => handleDownloadPdf(data)}
          disabled={isRowDownloading}
          style={isRowDownloading ? { opacity: 0.8, cursor: 'not-allowed' } : undefined}
        >
          {isRowDownloading ? (
            <Label>
              <IntlMessages id="button.text.downloading" defaultMessage="Downloading..." />
            </Label>
          ) : (
            <DownloadIconWithoutBg />
          )}
        </IconButton>
      );
    },
  ];

  // effect
  useEffect(() => {
    getUserData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cvdModalProps = {
    setCvdFormData: setCvdFormData,
    cvdFormData: cvdFormData,
    cvdModal: cvdModal,
    toggleCvdModal: toggleCvdModal,
    fetchData: query?.data,
    isSubmitting: isSubmitting,
    setIsSubmitting: setIsSubmitting,
  };

  const getCvdModal = () => {
    switch (process.env.REACT_APP_ENV) {
      case 'development':
        return <UsaAddCvdModal key={cvdModalKey} {...cvdModalProps} />;
      case 'stage':
        return <UsaAddCvdModal key={cvdModalKey} {...cvdModalProps} />;
      case 'production':
        return <UsaAddCvdModal key={cvdModalKey} {...cvdModalProps} />;
      case 'development_au':
        return <AusAddCvdModal key={cvdModalKey} {...cvdModalProps} />;
      case 'stage_au':
        return <AusAddCvdModal key={cvdModalKey} {...cvdModalProps} />;
      case 'production_au':
        return <AusAddCvdModal key={cvdModalKey} {...cvdModalProps} />;
      default:
        return <AusAddCvdModal key={cvdModalKey} {...cvdModalProps} />;
    }
  };

  if (query.isLoading) {
    return (
      <LoaderWrapper>
        <Loader />
      </LoaderWrapper>
    );
  }
  return (
    <StyledContainer>
      <CompanyContainer>
        <HeaderTitle>
          <IntlMessages id="nav.cvd" />
        </HeaderTitle>
        {!loader && canCreateEdec && (
          <IconButtonWithText
            text={<IntlMessages id="button.text.createCVD" />}
            icon={<AddPlusIcon />}
            onClick={toggleCvdModal}
          />
        )}
      </CompanyContainer>
      <TableWrapper>
        <Table
          data={tableData}
          columns={userColumns}
          hasFooter={true}
          actions={actionList}
          page={page}
          pageSize={pageSize}
          prevPage={handlePreviousPage}
          nextPage={handleNextPage}
          total={query?.data?.length || 0}
        />
      </TableWrapper>{' '}
      {!loader && getCvdModal()}
    </StyledContainer>
  );
};

export default Cvd;
